<script setup lang="ts">
import { watch } from "vue";
import { Radar, Wind, Settings2 } from "@lucide/vue";
import { useClickOutside } from "@/composables/useClickOutside";
import MapLayerControl from "./MapLayerControl.vue";
import { Button } from "@/components/ui/button";
import type { RadarSource } from "@/utils/radarProvider";

defineProps<{
  showRadarLayer: boolean;
  radarOpacity: number;
  isWindLayerActive: boolean;
  windParticleOpacity: number;
  radarSourceOverride?: RadarSource;
  activeRadarSource: RadarSource;
  radarFrameCount: number;
}>();

const emit = defineEmits<{
  "open-change": [open: boolean];
  "toggle-radar": [];
  "toggle-wind": [];
  "update-radar-opacity": [value: number];
  "update-wind-opacity": [value: number];
  "update-radar-source": [source: RadarSource | undefined];
}>();

const showRadarSourceControl = import.meta.env.DEV;
const radarSourceLabels: Record<RadarSource, string> = {
  weathercom: "weather.com",
  nws: "NWS",
  rainviewer: "RainViewer",
};
const { isOpen: showMenu, containerRef } = useClickOutside();

/** Apply a local provider choice, or return to automatic source selection. */
function updateRadarSource(event: Event) {
  const value = (event.target as HTMLSelectElement).value;
  emit("update-radar-source", value === "auto" ? undefined : (value as RadarSource));
}

watch(showMenu, (open) => emit("open-change", open));

// containerRef is used as a template ref (ref="containerRef")
defineExpose({ containerRef });
</script>

<template>
  <div ref="containerRef" class="relative">
    <Button
      variant="control"
      size="icon-compact"
      class="shrink-0"
      :class="
        showMenu
          ? 'bg-app-selected text-text-primary'
          : 'bg-transparent text-text-muted'
      "
      title="Layer settings"
      @click.stop="showMenu = !showMenu"
    >
      <Settings2 class="h-3.5 w-3.5" />
    </Button>
    <div
      v-if="showMenu"
      class="absolute right-0 bottom-[calc(100%+10px)] flex min-w-[200px] flex-col gap-2 rounded-small border border-app-border bg-app-material px-3 py-2.5"
      @click.stop
    >
      <MapLayerControl
        label="Radar"
        :icon="Radar"
        :active="showRadarLayer"
        :opacity="radarOpacity"
        @toggle="emit('toggle-radar')"
        @update:opacity="emit('update-radar-opacity', $event)"
      />
      <MapLayerControl
        label="Wind"
        :icon="Wind"
        :active="isWindLayerActive"
        :opacity="windParticleOpacity"
        @toggle="emit('toggle-wind')"
        @update:opacity="emit('update-wind-opacity', $event)"
      />
      <div
        v-if="showRadarSourceControl"
        class="mt-1 flex flex-col gap-1.5 border-t border-app-border pt-2 text-xs text-text-muted"
      >
        <label class="flex flex-col gap-1.5">
          <span>Radar source <small class="text-[10px]">(local only)</small></span>
          <select
            class="w-full rounded-small border border-app-border bg-app-selected px-2 py-1.5 text-xs text-text-primary"
            :value="radarSourceOverride ?? 'auto'"
            @change="updateRadarSource"
          >
            <option value="auto">Auto</option>
            <option value="weathercom">weather.com</option>
            <option value="nws">NWS</option>
            <option value="rainviewer">RainViewer</option>
          </select>
        </label>
        <span v-if="radarFrameCount === 0">No frames available from this source</span>
        <span v-else>Showing {{ radarSourceLabels[activeRadarSource] }}</span>
        <span v-if="activeRadarSource === 'rainviewer'">
          RainViewer displays through zoom 7.
        </span>
      </div>
    </div>
  </div>
</template>
